import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { saveAs } from 'file-saver';
import { apiService } from '../../../services/ApiService';

// Formatação de valores em moeda brasileira
const formatarValor = (valor) => {
  if (valor === null || valor === undefined || isNaN(valor)) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(valor);
};

// Quebra um texto em múltiplas linhas
const quebrarTexto = (texto, maxCaracteres) => {
  if (!texto || texto.length <= maxCaracteres) return [texto || ''];

  const linhas = [];
  let linhaAtual = '';
  const palavras = texto.split(' ');

  for (const palavra of palavras) {
    const novaLinha = linhaAtual ? linhaAtual + ' ' + palavra : palavra;

    if (novaLinha.length <= maxCaracteres) {
      linhaAtual = novaLinha;
    } else {
      linhas.push(linhaAtual);
      linhaAtual = palavra;
    }
  }

  if (linhaAtual) {
    linhas.push(linhaAtual);
  }

  return linhas;
};

// Função auxiliar para converter cor hex para RGB
const colorToRgb = (hexColor) => {
  if (!hexColor) return rgb(0, 0, 0);
  const r = parseInt(hexColor.slice(1, 3), 16) / 255;
  const g = parseInt(hexColor.slice(3, 5), 16) / 255;
  const b = parseInt(hexColor.slice(5, 7), 16) / 255;
  return rgb(r, g, b);
};

// Verificar se a URL é do Firebase Storage
const isFirebaseStorageUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  return url.includes('firebasestorage.googleapis.com') || 
         url.includes('storage.googleapis.com') ||
         url.includes('.firebasestorage.app');
};

// Função para baixar imagem através de um endpoint de proxy local em desenvolvimento
const baixarImagemViaProxyLocal = async (url) => {
  try {
    // Verificar se estamos em ambiente de desenvolvimento (localhost)
    const isLocalhost = window.location.hostname === 'localhost' || 
                        window.location.hostname === '127.0.0.1';
    
    if (!isLocalhost) {
      throw new Error('Proxy local só disponível em desenvolvimento');
    }
    
    // Criar um endpoint de proxy local no servidor de desenvolvimento
    // Isso assume que você tem um endpoint em /api/proxy no seu servidor de desenvolvimento
    const proxyUrl = `/api/proxy?url=${encodeURIComponent(url)}`;
    
    
    
    const response = await fetch(proxyUrl, {
      method: 'GET',
      headers: {
        'Cache-Control': 'no-cache'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Proxy local retornou erro: ${response.status}`);
    }
    
    const blob = await response.blob();
    return blob;
  } catch (error) {
    
    throw error;
  }
};

// Função auxiliar para converter uma URL do Firebase Storage para uma URL direta sem token
const converterParaURLDireta = (url) => {
  try {
    // Exemplo de transformação para URLs do Firebase Storage
    if (url.includes('firebasestorage.googleapis.com') || url.includes('.firebasestorage.app')) {
      // Extrair o caminho do arquivo sem parâmetros de autenticação
      const urlObj = new URL(url);
      const pathMatch = urlObj.pathname.match(/\/o\/([^?]+)/);
      
      if (pathMatch && pathMatch[1]) {
        // Caminho do arquivo codificado em URL
        const encodedPath = pathMatch[1];
        // Decodificar o caminho
        const path = decodeURIComponent(encodedPath);
        
        
        
        // Construir URL direta (pode não funcionar sem autenticação)
        return `https://storage.googleapis.com/${urlObj.hostname}/${path}`;
      }
    }
    
    // Retornar a URL original se não for possível converter
    return url;
  } catch (error) {
    
    return url;
  }
};

// Função para baixar imagem de registrosObra/fotos
const baixarImagemRegistroObra = async (obraId, fotoId) => {
  try {
    
    
    // URL do endpoint para acessar a imagem
    const url = `/diarioObras/${obraId}/fotos/${fotoId}/arquivo`;
    
    // Baixar a imagem
    const response = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        'Cache-Control': 'no-cache'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Erro ao baixar imagem: ${response.status} ${response.statusText}`);
    }
    
    // Obter o tipo de conteúdo
    const contentType = response.headers.get('Content-Type');
    
    // Converter para blob
    const blob = await response.blob();
    
    // Criar URL de objeto
    return {
      url: URL.createObjectURL(blob),
      contentType
    };
  } catch (error) {
    
    throw error;
  }
};

// Função auxiliar para carregar imagem de URL
const carregarImagemDeURL = async (url, pdfDoc, obraId = null, fotoId = null) => {
  try {
    
    
    // Verificar se é URL de registro de obra
    if (url && url.startsWith('/diarioObras/') && url.includes('/fotos/')) {
      try {
        // Extrair IDs da URL de registro de obra
        const segments = url.split('/');
        const obraIdFromUrl = segments[2];
        const fotoIdFromUrl = segments[4];
        
        // Usar o ID da obra e da foto fornecidos ou extraídos da URL
        const finalObraId = obraId || obraIdFromUrl;
        const finalFotoId = fotoId || fotoIdFromUrl;
        
        // Baixar a imagem
        const imagem = await baixarImagemRegistroObra(finalObraId, finalFotoId);
        
        // Incorporar no PDF de acordo com o tipo
        const isPng = imagem.contentType.includes('image/png');
        
        // Converter Blob URL para dado binário
        const respBinary = await fetch(imagem.url);
        const imageData = await respBinary.arrayBuffer();
        
        return isPng 
          ? await pdfDoc.embedPng(imageData)
          : await pdfDoc.embedJpg(imageData);
          
      } catch (error) {
        
        return await criarImagemPlaceholder('Erro ao carregar imagem do registro', pdfDoc);
      }
    }
    
    // Verificar se já é um data URL
    if (url && url.startsWith('data:')) {
      try {
        // Extrair o tipo de imagem e os dados
        const parts = url.split(',');
        const base64Data = parts[1];
        const contentType = parts[0].split(':')[1].split(';')[0];
        const isPng = contentType.includes('image/png');
        
        // Incorporar diretamente no PDF
        return isPng 
          ? await pdfDoc.embedPng(base64Data)
          : await pdfDoc.embedJpg(base64Data);
      } catch (error) {
        
        return await criarImagemPlaceholder('Erro ao processar imagem', pdfDoc);
      }
    }
    
    // Detectar se é uma URL do Firebase Storage
    const isFirebaseUrl = isFirebaseStorageUrl(url);
    
    if (isFirebaseUrl) {
      try {
        // Tentar carregar a imagem usando um elemento Image
        const img = await new Promise((resolve, reject) => {
          const imgEl = new Image();
          imgEl.crossOrigin = 'anonymous';
          
          imgEl.onload = () => resolve(imgEl);
          imgEl.onerror = (e) => reject(new Error('Falha ao carregar imagem'));
          
          imgEl.src = url;
        });
        
        // Converter para data URL via canvas
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        
        const dataURL = canvas.toDataURL('image/png');
        const parts = dataURL.split(',');
        const base64Data = parts[1];
        
        // Incorporar no PDF
        return await pdfDoc.embedPng(base64Data);
      } catch (error) {
        
        return await criarImagemPlaceholder('Imagem não pôde ser carregada', pdfDoc);
      }
    }
    
    // Para URLs normais, tentar fetch com no-cors
    try {
      const response = await fetch(url, {
        method: 'GET',
        mode: 'no-cors',
        cache: 'no-store'
      });
      
      if (response.ok) {
        const imageData = await response.arrayBuffer();
        const contentType = response.headers.get('Content-Type');
        const isPng = contentType?.includes('image/png') || url.toLowerCase().endsWith('.png');
        
        return isPng 
          ? await pdfDoc.embedPng(imageData)
          : await pdfDoc.embedJpg(imageData);
      }
    } catch (error) {
      
    }
    
    // Se todos os métodos falharem, criar um placeholder
    return await criarImagemPlaceholder('Imagem não pôde ser carregada', pdfDoc);
    
  } catch (error) {
    
    return await criarImagemPlaceholder('Erro ao processar imagem', pdfDoc);
  }
};

// Função para obter dados da obra do objeto recebido, considerando vários caminhos possíveis
const getDadosObra = (dadosObra, campo) => {
  if (!dadosObra) return '-';
  
  // Possíveis caminhos para os dados dependendo da estrutura do backend
  const possiveisCaminhos = {
    dataInicio: ['dataInicio', 'dados.dataInicio', 'cronograma.dataInicio', 'datas.inicio'],
    dataFim: ['dataPrevisaoFim', 'dados.dataPrevisaoFim', 'cronograma.dataPrevisaoFim', 'datas.previsaoFim'],
    local: ['endereco', 'dados.endereco', 'dadosGerais.endereco', 'localizacao.endereco', 'informacoes.endereco'],
    contratante: ['cliente', 'dados.cliente', 'dadosGerais.cliente', 'informacoes.cliente'],
    responsavel: ['responsavel', 'engenheiroResponsavel', 'dados.responsavel', 'dados.engenheiroResponsavel', 
                  'dadosGerais.responsavel', 'equipe.responsavel']
  };
  
  // Obter o caminho correto para o campo solicitado
  const caminhos = possiveisCaminhos[campo] || [];
  
  // Tentar cada caminho possível
  for (const caminho of caminhos) {
    const partes = caminho.split('.');
    let valor = dadosObra;
    
    for (const parte of partes) {
      if (valor && typeof valor === 'object' && parte in valor) {
        valor = valor[parte];
      } else {
        valor = undefined;
        break;
      }
    }
    
    if (valor !== undefined && valor !== null && valor !== '') {
      return valor;
    }
  }
  
  // Verificar se o campo está diretamente na raiz do objeto
  if (campo in dadosObra && dadosObra[campo] !== undefined && dadosObra[campo] !== null && dadosObra[campo] !== '') {
    return dadosObra[campo];
  }
  
  return '-';
};

// Função para criar uma imagem placeholder com mensagem
const criarImagemPlaceholder = async (mensagem, pdfDoc) => {
  try {
    // Criar um canvas para gerar uma imagem de placeholder
    const canvas = document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 200;
    const ctx = canvas.getContext('2d');
    
    // Desenhar um fundo cinza claro
    ctx.fillStyle = '#f5f5f5';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Adicionar uma borda
    ctx.strokeStyle = '#dddddd';
    ctx.lineWidth = 4;
    ctx.strokeRect(10, 10, canvas.width - 20, canvas.height - 20);
    
    // Desenhar um ícone de imagem (representação simples)
    ctx.strokeStyle = '#999999';
    ctx.lineWidth = 2;
    // Desenhar frame da imagem
    ctx.strokeRect(canvas.width/2 - 30, 60, 60, 40);
    // Desenhar "montanha" dentro do frame
    ctx.beginPath();
    ctx.moveTo(canvas.width/2 - 20, 90);
    ctx.lineTo(canvas.width/2 - 10, 75);
    ctx.lineTo(canvas.width/2 + 5, 85);
    ctx.lineTo(canvas.width/2 + 20, 65);
    ctx.lineTo(canvas.width/2 + 30, 90);
    ctx.closePath();
    ctx.stroke();
    
    // Adicionar a mensagem
    ctx.fillStyle = '#666666';
    ctx.font = '14px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // Quebrar a mensagem em linhas se for muito grande
    const palavras = mensagem.split(' ');
    let linhas = [];
    let linhaAtual = '';
    
    for (const palavra of palavras) {
      const testarLinha = linhaAtual ? `${linhaAtual} ${palavra}` : palavra;
      if (testarLinha.length <= 35) {
        linhaAtual = testarLinha;
      } else {
        linhas.push(linhaAtual);
        linhaAtual = palavra;
      }
    }
    
    if (linhaAtual) {
      linhas.push(linhaAtual);
    }
    
    // Desenhar as linhas de texto
    let y = 130;
    for (const linha of linhas) {
      ctx.fillText(linha, canvas.width/2, y);
      y += 20;
    }
    
    // Obter a imagem como data URL
    const dataURL = canvas.toDataURL('image/png');
    const parts = dataURL.split(',');
    const base64Data = parts[1];
    
    // Incorporar no PDF
    return await pdfDoc.embedPng(base64Data);
  } catch (error) {
    
    throw new Error('Não foi possível criar imagem placeholder');
  }
};

// Função para formatar data no padrão brasileiro, considerando o fuso horário
const formatarData = (dataString) => {
  if (!dataString) return '-';
  try {
    // Extrair ano, mês e dia da string de data
    const [ano, mes, dia] = dataString.split('-');
    // Criar data com horário fixo no meio do dia (12:00:00) para evitar problemas de fuso horário
    const data = new Date(ano, mes - 1, dia, 12, 0, 0);
    return data.toLocaleDateString('pt-BR');
  } catch (error) {
    console.error('Erro ao formatar data:', error);
    return '-';
  }
};

const gerarRelatorioDiarioObraPDF = async ({
  logoUrl,
  corPrimaria = '#0077B6',
  tituloRDO = 'Relatório Diário de Obra',
  dataInicioObra,
  dataFimObra,
  localObra,
  contratante,
  responsavelTecnico,
  dadosRelatorio = {},
  dataSelecionada = null,
  nome,
  // Dados da obra completos para extrair as informações
  ...dadosObra
}) => {
  try {
    // Buscar configurações de diário de obra
    let configDiarioObra = null;
    try {
      const resultado = await apiService.getDiarioObraConfiguracao();
      if (resultado.success && resultado.data) {
        configDiarioObra = resultado.data;
        
        
        // Usar a cor primária da configuração, se disponível
        if (configDiarioObra.corPrimaria) {
          corPrimaria = configDiarioObra.corPrimaria;
          
        }
        
        // Usar o logo da empresa das configurações, se disponível e se não foi fornecido um logoUrl específico
        if (!logoUrl && configDiarioObra.logoEmpresas) {
          logoUrl = configDiarioObra.logoEmpresas;
          
        }
      }
    } catch (configError) {
      
      // Continuar com as configurações padrão se não conseguir carregar
    }
    
    // Extrair imagens para processamento
    let fotosPre = [];
    if (dadosRelatorio.fotos && Array.isArray(dadosRelatorio.fotos) && dadosRelatorio.fotos.length > 0) {
      
      
      // Função auxiliar para extrair a URL da foto de qualquer estrutura possível
      const extrairURLFoto = (foto) => {
        if (!foto) return null;
        
        // Verificar todas as possíveis localizações da URL
        if (typeof foto === 'string') return foto;
        
        if (typeof foto.url === 'string' && foto.url) {
          return foto.url;
        }
        
        if (foto.arquivo) {
          if (typeof foto.arquivo === 'string') return foto.arquivo;
          if (typeof foto.arquivo.url === 'string' && foto.arquivo.url) {
            return foto.arquivo.url;
          }
          if (typeof foto.arquivo.dataUri === 'string' && foto.arquivo.dataUri) {
            return foto.arquivo.dataUri;
          }
        }
        
        if (foto.dados && foto.dados.url) {
          return foto.dados.url;
        }
        
        if (foto.dados && foto.dados.arquivo) {
          if (typeof foto.dados.arquivo === 'string') return foto.dados.arquivo;
          if (typeof foto.dados.arquivo.url === 'string') {
            return foto.dados.arquivo.url;
          }
          if (typeof foto.dados.arquivo.dataUri === 'string') {
            return foto.dados.arquivo.dataUri;
          }
        }
        
        // Em último caso, tentar fazer parsing caso seja uma string JSON
        if (foto.dados && typeof foto.dados === 'string') {
          try {
            const dadosObj = JSON.parse(foto.dados);
            if (dadosObj.url) return dadosObj.url;
            if (dadosObj.arquivo && dadosObj.arquivo.url) return dadosObj.arquivo.url;
          } catch (e) {
            // Ignorar erro de parsing
          }
        }
        
        // Verificar campos que possam conter caminho de arquivo
        if (foto.caminho) return foto.caminho;
        if (foto.path) return foto.path;
        if (foto.imagemUrl) return foto.imagemUrl;
        if (foto.src) return foto.src;
        
        // Verificar se há campos específicos para o Firebase Storage ou logoEmpresas
        if (foto.logoEmpresas) return foto.logoEmpresas;
        if (foto.storageUrl) return foto.storageUrl;
        
        return null;
      };
      
      // Preparar cada foto
      const fotosParaProcessar = dadosRelatorio.fotos
        .filter(foto => extrairURLFoto(foto) !== null)
        .map(foto => ({
          ...foto,
          urlExtraida: extrairURLFoto(foto)
        }));
        
      for (const foto of fotosParaProcessar) {
        fotosPre.push({
          ...foto,
          urlImagem: foto.urlExtraida,  // Guardar URL original para carregar depois
          processada: true
        });
      }
      
      
    }
    
    // Continuar com a geração do PDF
    const pdfDoc = await PDFDocument.create();
    let page = pdfDoc.addPage([595.28, 841.89]); // A4
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

    const margin = 50;
    const pageWidth = page.getWidth() - 2 * margin;
    let y = page.getHeight() - margin;

    // Função para desenhar texto
    const drawText = (text, x, y, fontType = font, size = 10, color = rgb(0, 0, 0)) => {
      if (!text) text = '-';
      page.drawText(text, { x, y, font: fontType, size, color });
    };

    // Função para desenhar uma linha
    const drawLine = (x1, y1, x2, y2, color = rgb(0.8, 0.8, 0.8), thickness = 0.5) => {
      page.drawLine({ start: { x: x1, y: y1 }, end: { x: x2, y: y2 }, color, thickness });
    };

    // Cabeçalho
    if (logoUrl) {
      try {
        // Carregar logo usando a função auxiliar
        const logo = await carregarImagemDeURL(logoUrl, pdfDoc);
        
        const logoAspectRatio = logo.width / logo.height;
        
        // Ajustar tamanho e posição do logo
        const logoMaxWidth = 150; // Largura máxima para o logo
        const logoMaxHeight = 50; // Altura máxima para o logo
        
        let logoWidth, logoHeight;
        
        if (logoAspectRatio > 1) {
          // Logo mais largo que alto
          logoWidth = Math.min(logoMaxWidth, logo.width);
          logoHeight = logoWidth / logoAspectRatio;
        } else {
          // Logo mais alto que largo
          logoHeight = Math.min(logoMaxHeight, logo.height);
          logoWidth = logoHeight * logoAspectRatio;
        }
        
        // Centralizar logo horizontalmente
        const centerX = (page.getWidth() - logoWidth) / 2;
        
        // Desenhar logo no topo centralizado
        page.drawImage(logo, { 
          x: centerX, 
          y: y - logoHeight, 
          width: logoWidth, 
          height: logoHeight 
        });
        
        // Atualizar posição y para o próximo elemento após o logo
        y -= (logoHeight + 20);
        
      } catch (error) {
        
        // Continuar sem a logo
      }
    }

    // Adiciona título do relatório com a data selecionada, se houver
    const tituloRelatorio = dataSelecionada 
      ? `Relatório Diário de Obra - ${formatarData(dataSelecionada)}`
      : 'Relatório Diário de Obra';
    
    // Centralizar título
    const titleFontSize = 18;
    const titleWidth = tituloRelatorio.length * (titleFontSize * 0.5);
    const titleX = (page.getWidth() - titleWidth) / 2;
    
    drawText(tituloRelatorio, titleX, y - 10, fontBold, titleFontSize, colorToRgb(corPrimaria));
    y -= 25;

    drawLine(margin, y, pageWidth + margin, y, colorToRgb(corPrimaria), 1.5);
    y -= 25;

    // Obter dados da obra do objeto recebido
    const dataInicio = dataInicioObra || getDadosObra(dadosObra, 'dataInicio');
    const dataInicioBR = formatarData(dataInicio);
    
    const dataFim = dataFimObra || getDadosObra(dadosObra, 'dataFim');
    const dataFimBR = formatarData(dataFim);
    
    const local = localObra || getDadosObra(dadosObra, 'local');
    const clienteObra = contratante || getDadosObra(dadosObra, 'contratante');
    const responsavel = responsavelTecnico || getDadosObra(dadosObra, 'responsavel');

    drawText(`Data Início: ${dataInicioBR}`, margin, y);
    drawText(`Data Fim: ${dataFimBR}`, margin + 200, y);
    y -= 15;
    drawText(`Local da Obra: ${local}`, margin, y);
    drawText(`Contratante: ${clienteObra}`, margin + 200, y);
    y -= 15;
    drawText(`Responsável Técnico: ${responsavel}`, margin, y);
    y -= 30;

    // Verificar se há dados para renderizar
    if (!dadosRelatorio || Object.keys(dadosRelatorio).length === 0) {
      drawText('Nenhum dado disponível para este relatório.', margin, y);
    } else {
      // Primeiro, processamos as fotos para incluí-las no PDF
      if (fotosPre.length > 0) {
        drawText('Fotos', margin, y, fontBold, 16, colorToRgb(corPrimaria));
        y -= 20;
        drawLine(margin, y, pageWidth + margin, y, colorToRgb(corPrimaria), 1);
        y -= 15;
        
        // Define layout para exibição de fotos (2 por linha)
        const fotosWidth = pageWidth / 2 - 20;
        const fotosHeight = 200;
        
        for (let i = 0; i < fotosPre.length; i += 2) {
          // Verifica se precisa de nova página para as fotos
          if (y - fotosHeight - 40 < margin) {
            page = pdfDoc.addPage([595.28, 841.89]);
            y = page.getHeight() - margin;
          }
          
          // Processa a primeira foto da linha
          const foto1 = fotosPre[i];
          
          if (foto1.processada) {
            try {
              // Carregar a imagem usando a função auxiliar
              const image = await carregarImagemDeURL(foto1.urlImagem, pdfDoc);
              
              // Calcular dimensões mantendo a proporção
              const imageAspectRatio = image.width / image.height;
              let drawWidth, drawHeight;
              
              if (imageAspectRatio > 1) {
                drawWidth = fotosWidth;
                drawHeight = fotosWidth / imageAspectRatio;
              } else {
                drawHeight = fotosHeight;
                drawWidth = fotosHeight * imageAspectRatio;
              }
              
              // Desenha a imagem
              page.drawImage(image, {
                x: margin,
                y: y - drawHeight,
                width: drawWidth,
                height: drawHeight,
              });
            } catch (error) {
              
              
              // Desenhar placeholder para foto com erro
              const grayColor = rgb(0.8, 0.8, 0.8);
              page.drawRectangle({
                x: margin,
                y: y - fotosHeight,
                width: fotosWidth,
                height: fotosHeight,
                color: grayColor,
                borderColor: rgb(0.6, 0.6, 0.6),
                borderWidth: 1,
              });
              
              drawText(
                "Erro ao processar imagem",
                margin + fotosWidth/2 - 70, 
                y - fotosHeight/2,
                font,
                10
              );
            }
          } else {
            // Desenhar placeholder para foto não processada
            const grayColor = rgb(0.8, 0.8, 0.8);
            page.drawRectangle({
              x: margin,
              y: y - fotosHeight,
              width: fotosWidth,
              height: fotosHeight,
              color: grayColor,
              borderColor: rgb(0.6, 0.6, 0.6),
              borderWidth: 1,
            });
            
            drawText(
              "Imagem não disponível",
              margin + fotosWidth/2 - 70, 
              y - fotosHeight/2,
              font,
              10
            );
          }
          
          // Adiciona legenda e data
          if (foto1.dados && foto1.dados.titulo) {
            drawText(
              foto1.dados.titulo, 
              margin, 
              y - fotosHeight - 15,
              font,
              9
            );
          }
          
          if (foto1.data) {
            const dataFoto = formatarData(foto1.data);
            drawText(
              dataFoto,
              margin, 
              y - fotosHeight - 25,
              font,
              8,
              rgb(0.5, 0.5, 0.5)
            );
          }
          
          // Processa a segunda foto da linha (se existir)
          if (i + 1 < fotosPre.length) {
            const foto2 = fotosPre[i + 1];
            
            if (foto2.processada) {
              try {
                // Carregar a imagem usando a função auxiliar
                const image = await carregarImagemDeURL(foto2.urlImagem, pdfDoc);
                
                // Calcular dimensões mantendo a proporção
                const imageAspectRatio = image.width / image.height;
                let drawWidth, drawHeight;
                
                if (imageAspectRatio > 1) {
                  drawWidth = fotosWidth;
                  drawHeight = fotosWidth / imageAspectRatio;
                } else {
                  drawHeight = fotosHeight;
                  drawWidth = fotosHeight * imageAspectRatio;
                }
                
                // Desenha a imagem
                page.drawImage(image, {
                  x: margin + fotosWidth + 40,
                  y: y - drawHeight,
                  width: drawWidth,
                  height: drawHeight,
                });
              } catch (error) {
                
                
                // Desenhar placeholder para foto com erro
                const grayColor = rgb(0.8, 0.8, 0.8);
                page.drawRectangle({
                  x: margin + fotosWidth + 40,
                  y: y - fotosHeight,
                  width: fotosWidth,
                  height: fotosHeight,
                  color: grayColor,
                  borderColor: rgb(0.6, 0.6, 0.6),
                  borderWidth: 1,
                });
                
                drawText(
                  "Erro ao processar imagem",
                  margin + fotosWidth + 40 + fotosWidth/2 - 70, 
                  y - fotosHeight/2,
                  font,
                  10
                );
              }
            } else {
              // Desenhar placeholder para foto não processada
              const grayColor = rgb(0.8, 0.8, 0.8);
              page.drawRectangle({
                x: margin + fotosWidth + 40,
                y: y - fotosHeight,
                width: fotosWidth,
                height: fotosHeight,
                color: grayColor,
                borderColor: rgb(0.6, 0.6, 0.6),
                borderWidth: 1,
              });
              
              drawText(
                "Imagem não disponível",
                margin + fotosWidth + 40 + fotosWidth/2 - 70, 
                y - fotosHeight/2,
                font,
                10
              );
            }
            
            // Adiciona legenda e data
            if (foto2.dados && foto2.dados.titulo) {
              drawText(
                foto2.dados.titulo, 
                margin + fotosWidth + 40, 
                y - fotosHeight - 15,
                font,
                9
              );
            }
            
            if (foto2.data) {
              const dataFoto = formatarData(foto2.data);
              drawText(
                dataFoto,
                margin + fotosWidth + 40, 
                y - fotosHeight - 25,
                font,
                8,
                rgb(0.5, 0.5, 0.5)
              );
            }
          }
          
          // Atualiza a posição Y para a próxima linha de fotos
          y -= fotosHeight + 40;
        }
        
        y -= 10; // Espaço adicional após a seção de fotos
      }
      
      // Renderizar as demais seções
      for (const key in dadosRelatorio) {
        // Pular se for a seção de fotos (já processada) ou se não houver dados
        if (key === 'fotos' || !dadosRelatorio[key] || !Array.isArray(dadosRelatorio[key]) || dadosRelatorio[key].length === 0) {
          continue;
        }

        const secaoData = dadosRelatorio[key];
        
        // Verificar se precisa adicionar nova página
        if (y < margin + 100) {
          page = pdfDoc.addPage([595.28, 841.89]);
          y = page.getHeight() - margin;
        }

        drawText(key.charAt(0).toUpperCase() + key.slice(1), margin, y, fontBold, 16, colorToRgb(corPrimaria));
        y -= 20;
        drawLine(margin, y, pageWidth + margin, y, colorToRgb(corPrimaria), 1);
        y -= 15;

        // Extrair campos de dados do primeiro item
        let primeiroItem = secaoData[0];
        if (!primeiroItem || !primeiroItem.dados || typeof primeiroItem.dados !== 'object') {
          drawText('Dados inválidos nesta seção.', margin, y);
          y -= 20;
          continue;
        }

        const headers = Object.keys(primeiroItem.dados).filter(header => 
          header !== 'undefined' && header !== 'null'
        );

        if (headers.length > 0) {
          let x = margin;
          const colWidth = pageWidth / Math.min(headers.length, 4); // Limitando a 4 colunas máximas
          
          // Desenhar cabeçalhos de colunas
          for (let i = 0; i < Math.min(headers.length, 4); i++) {
            const header = headers[i];
            drawText(
              header.charAt(0).toUpperCase() + header.slice(1), 
              x, 
              y, 
              fontBold, 
              10
            );
            x += colWidth;
          }
          
          y -= 15;
          drawLine(margin, y, pageWidth + margin, y);
          y -= 10;

          // Desenhar dados
          for (const item of secaoData) {
            if (!item || !item.dados) continue;
            
            let rowHeight = 0;
            
            // Primeiro, calcular a altura necessária para esta linha
            for (let i = 0; i < Math.min(headers.length, 4); i++) {
              const header = headers[i];
              let valor = item.dados[header];
              
              // Tratar diferentes tipos de dados
              let textoValor;
              if (Array.isArray(valor)) {
                // Se for mão de obra, tratar cada funcionário em uma nova linha
                if (header.toLowerCase().includes('mão de obra') || header.toLowerCase().includes('mao de obra')) {
                  textoValor = valor.map(func => `• ${func}`).join('\n');
                } else {
                  textoValor = valor.join(', ');
                }
              } else if (typeof valor === 'object' && valor !== null) {
                textoValor = JSON.stringify(valor);
              } else {
                textoValor = valor !== undefined && valor !== null ? String(valor) : '-';
              }
              
              const lines = quebrarTexto(textoValor, Math.floor(colWidth / 6));
              rowHeight = Math.max(rowHeight, lines.length * 12);
            }
            
            // Verificar se precisa de nova página para esta linha
            if (y - rowHeight - 10 < margin) {
              page = pdfDoc.addPage([595.28, 841.89]);
              y = page.getHeight() - margin;
              
              // Redesenhar cabeçalhos
              let x = margin;
              for (let i = 0; i < Math.min(headers.length, 4); i++) {
                const header = headers[i];
                drawText(
                  header.charAt(0).toUpperCase() + header.slice(1), 
                  x, 
                  y, 
                  fontBold, 
                  10
                );
                x += colWidth;
              }
              
              y -= 15;
              drawLine(margin, y, pageWidth + margin, y);
              y -= 10;
            }
            
            // Agora desenhar os dados
            let cellY = y;
            for (let i = 0; i < Math.min(headers.length, 4); i++) {
              const header = headers[i];
              let valor = item.dados[header];
              
              // Tratar diferentes tipos de dados
              let textoValor;
              if (Array.isArray(valor)) {
                // Se for mão de obra, tratar cada funcionário em uma nova linha
                if (header.toLowerCase().includes('mão de obra') || header.toLowerCase().includes('mao de obra')) {
                  textoValor = valor.map(func => `• ${func}`).join('\n');
                } else {
                  textoValor = valor.join(', ');
                }
              } else if (typeof valor === 'object' && valor !== null) {
                textoValor = JSON.stringify(valor);
              } else {
                textoValor = valor !== undefined && valor !== null ? String(valor) : '-';
              }
              
              const lines = quebrarTexto(textoValor, Math.floor(colWidth / 6));
              
              lines.forEach((line, index) => {
                drawText(line, margin + (i * colWidth), cellY - (index * 12), font, 9);
              });
            }
            
            // Desenhar linha separadora após o conteúdo
            y -= rowHeight + 5;
            drawLine(margin, y, pageWidth + margin, y);
          }
          
          y -= 20;
        } else {
          drawText('Nenhum dado disponível nesta seção.', margin, y);
          y -= 20;
        }
      }
    }

    // Antes de finalizar o relatório, verificar se o dia está aprovado
    // e adicionar informações de aprovação e assinaturas
    if (dataSelecionada) {
      // Verificar aprovação do dia
      try {
        const resultado = await apiService.verificarAprovacaoRDO(
          dadosObra.id, 
          dataSelecionada
        );
        
        if (resultado.success && resultado.aprovado && resultado.dados) {
          const dadosAprovacao = resultado.dados;
          
          // Adicionar uma nova página para as aprovações
          page = pdfDoc.addPage([595.28, 841.89]);
          y = page.getHeight() - margin;
          
          // Título para seção de aprovação
          drawText("APROVAÇÃO DO RELATÓRIO DIÁRIO DE OBRA", margin, y, fontBold, 16, colorToRgb(corPrimaria));
          y -= 30;
          
          // Linha separadora
          drawLine(margin, y, pageWidth + margin, y, rgb(0.7, 0.7, 0.7), 1);
          y -= 30;
          
          // Informações gerais de aprovação
          drawText("Informações de Aprovação:", margin, y, fontBold, 12);
          y -= 20;
          
          // Data de aprovação
          if (dadosAprovacao.dataAprovacao) {
            drawText(`Data da Aprovação: ${formatarData(dadosAprovacao.dataAprovacao)}`, margin, y);
            y -= 15;
          }
          
          // Observações
          if (dadosAprovacao.observacoes) {
            drawText("Observações:", margin, y, fontBold, 12);
            y -= 15;
            drawText(dadosAprovacao.observacoes, margin, y);
            y -= 30;
          }
          
          // Seção de Aprovadores
          drawText("Aprovadores:", margin, y, fontBold, 14, colorToRgb(corPrimaria));
          y -= 30;
          
          // Aprovador Responsável Técnico
          drawText("Responsável Técnico:", margin, y, fontBold, 12);
          y -= 15;
          drawText(`Nome: ${dadosAprovacao.nomeUsuario || "Não informado"}`, margin + 20, y);
          y -= 15;
          drawText(`Email: ${dadosAprovacao.emailUsuario || "Não informado"}`, margin + 20, y);
          y -= 15;
          if (dadosAprovacao.dataAprovacao) {
            drawText(`Data: ${formatarData(dadosAprovacao.dataAprovacao)}`, margin + 20, y);
          }
          y -= 30;
          
          // Se houver aprovação do cliente
          if (dadosAprovacao.clienteAprovacao) {
            drawText("Cliente:", margin, y, fontBold, 12);
            y -= 15;
            drawText(`Nome: ${dadosAprovacao.clienteNomeUsuario || "Não informado"}`, margin + 20, y);
            y -= 15;
            drawText(`Email: ${dadosAprovacao.clienteEmailUsuario || "Não informado"}`, margin + 20, y);
            y -= 15;
            if (dadosAprovacao.clienteDataAprovacao) {
              drawText(`Data: ${formatarData(dadosAprovacao.clienteDataAprovacao)}`, margin + 20, y);
            }
            y -= 15;
            
            // Observações do cliente
            if (dadosAprovacao.clienteObservacoes) {
              drawText("Observações do Cliente:", margin, y, fontBold, 12);
              y -= 15;
              drawText(dadosAprovacao.clienteObservacoes, margin + 20, y);
            }
          }
        }
      } catch (approvalError) {
        
        // Continuar sem as informações de aprovação
      }
    }

    // Rodapé
    drawLine(margin, margin - 20, pageWidth + margin, margin - 20);
    drawText(`Gerado em: ${formatarData(new Date().toISOString().split('T')[0])}`, margin, margin - 30);

    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    
    // Formatar o nome do arquivo usando a data do relatório
    const dataFormatada = dataSelecionada 
      ? formatarData(dataSelecionada).replace(/\//g, '-')
      : new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
    
    saveAs(blob, `relatorio_diario_obra_${dataFormatada}.pdf`);
    
    return true;
  } catch (error) {
    
    alert('Erro ao gerar o PDF. Por favor, tente novamente.');
    return false;
  }
};

export default gerarRelatorioDiarioObraPDF; 